import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { RefreshCw, Trash2, Shield, Activity, Users, Mic } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, hint, Icon }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-5">
      <div className="flex items-start justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
          {label}
        </span>
        {Icon && <Icon className="h-4 w-4 text-[#002FA7]" />}
      </div>
      <div className="mt-3 font-serif text-3xl text-zinc-950 tabular-nums">
        {value ?? "—"}
      </div>
      {hint && (
        <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
          {hint}
        </div>
      )}
    </div>
  );
}

export default function AdminPanel() {
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [voices, setVoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      const [s, v, j, l] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/voices`),
        axios.get(`${API}/admin/jobs?limit=25`),
        axios.get(`${API}/admin/logs?limit=15`),
      ]);
      setStats(s.data);
      setVoices(v.data.voices || []);
      setJobs(j.data.jobs || []);
      setLogs(l.data.errors || []);
    } catch (err) {
      toast.error("Failed to load admin data", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusy(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!isAdmin) return null;

  const deleteVoice = async (voice_id) => {
    if (!window.confirm(`Delete voice ${voice_id}?`)) return;
    try {
      await axios.delete(`${API}/admin/voices/${voice_id}`);
      toast.success("Voice deleted");
      refresh();
    } catch (err) {
      toast.error("Delete failed", {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  return (
    <section
      id="admin"
      data-testid="admin-panel"
      className="relative border-t border-zinc-200 bg-zinc-50/50"
    >
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#002FA7] font-semibold">
              <Shield className="h-3.5 w-3.5" /> Admin panel
            </span>
            <h2 className="mt-3 font-serif text-4xl lg:text-5xl leading-tight text-zinc-950">
              Platform overview
            </h2>
          </div>
          <button
            data-testid="admin-refresh"
            onClick={refresh}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-4 py-2 font-sans text-sm font-medium text-zinc-800 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-60 transition-colors duration-200"
          >
            <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard
            label="Jobs today"
            value={stats?.today?.jobs?.toLocaleString?.() ?? "—"}
            hint={`${stats?.today?.chars?.toLocaleString?.() ?? 0} chars`}
            Icon={Activity}
          />
          <StatCard
            label="Jobs this month"
            value={stats?.month?.jobs?.toLocaleString?.() ?? "—"}
            hint={`${stats?.month?.chars?.toLocaleString?.() ?? 0} chars`}
            Icon={Activity}
          />
          <StatCard
            label="Unique users 30d"
            value={stats?.totals?.unique_users_30d ?? "—"}
            hint={`${stats?.totals?.jobs ?? 0} total jobs`}
            Icon={Users}
          />
          <StatCard
            label="Cloned voices"
            value={stats?.totals?.cloned_voices ?? "—"}
            hint={
              stats?.heygen?.key_configured
                ? "HeyGen: connected"
                : "HeyGen: not configured"
            }
            Icon={Mic}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Voices */}
          <div className="rounded-xl border border-zinc-200 bg-white p-6">
            <h3 className="font-serif text-xl text-zinc-950 mb-4">
              All cloned voices
            </h3>
            <div className="max-h-72 overflow-y-auto">
              {voices.length === 0 ? (
                <p className="font-sans text-sm text-zinc-500">No voices yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="text-left py-1">Name</th>
                      <th className="text-left py-1">Status</th>
                      <th className="text-left py-1">Owner</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody
                    data-testid="admin-voices-tbody"
                    className="font-sans text-sm text-zinc-800"
                  >
                    {voices.slice(0, 40).map((v) => (
                      <tr key={v.voice_id} className="border-t border-zinc-100">
                        <td className="py-2 pr-2 truncate max-w-[10rem]">{v.name}</td>
                        <td className="py-2 pr-2">
                          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                            {v.status}
                          </span>
                        </td>
                        <td className="py-2 pr-2 font-mono text-[10px] truncate max-w-[10rem]">
                          {v.user_email ? (
                            <span className="text-[#002FA7]">{v.user_email}</span>
                          ) : (
                            <span className="text-zinc-400">anon · {v.session_id?.slice(0, 8)}…</span>
                          )}
                        </td>
                        <td className="py-2">
                          <button
                            data-testid={`admin-delete-voice-${v.voice_id}`}
                            onClick={() => deleteVoice(v.voice_id)}
                            className="text-zinc-500 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Jobs */}
          <div className="rounded-xl border border-zinc-200 bg-white p-6">
            <h3 className="font-serif text-xl text-zinc-950 mb-4">Latest jobs</h3>
            <div className="max-h-72 overflow-y-auto">
              {jobs.length === 0 ? (
                <p className="font-sans text-sm text-zinc-500">No jobs yet.</p>
              ) : (
                <ul
                  data-testid="admin-jobs-list"
                  className="divide-y divide-zinc-100 text-sm"
                >
                  {jobs.slice(0, 25).map((j) => (
                    <li key={j.id} className="py-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-mono text-[10px] text-zinc-500 truncate">
                          {j.id.slice(0, 8)} · {j.provider} · {j.voice}
                        </div>
                        <div className="text-zinc-700 truncate">
                          {j.chars} chars · {j.done}/{j.chunks} chunks
                        </div>
                      </div>
                      <span
                        className={`font-mono text-[10px] uppercase tracking-widest ${
                          j.status === "error"
                            ? "text-red-600"
                            : j.status === "done" || j.status === "downloaded"
                            ? "text-emerald-700"
                            : "text-amber-700"
                        }`}
                      >
                        {j.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Errors */}
          <div className="lg:col-span-2 rounded-xl border border-zinc-200 bg-white p-6">
            <h3 className="font-serif text-xl text-zinc-950 mb-4">
              Recent errors ({logs.length})
            </h3>
            {logs.length === 0 ? (
              <p className="font-sans text-sm text-emerald-700">
                No errors in recent jobs. 🎉
              </p>
            ) : (
              <ul
                data-testid="admin-logs-list"
                className="divide-y divide-zinc-100 text-sm"
              >
                {logs.map((l) => (
                  <li key={l.job_id} className="py-2">
                    <span className="font-mono text-[10px] text-zinc-400">
                      {l.created_at} · {l.provider}
                    </span>
                    <div className="text-red-700">{l.error}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
